import { EMPTY_STRING } from 'constants/general';
import {
  DetailedHTMLProps,
  InputHTMLAttributes,
  useEffect,
  useRef,
  useState
} from 'react';
import { t } from 'services/i18n.service';
import { v4 as uuidv4 } from 'uuid';

interface EmailInputProps extends DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> {
  readonly additionalStyles?: string;
  hasError?: boolean;
  error?: string;
  id?: string;
  label?: string;
}

const EmailInput = ({
  hasError = false,
  additionalStyles,
  type = 'email',
  placeholder,
  error,
  id,
  label,
  ...rest
}: EmailInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = id || uuidv4();
  const isInValidInput = hasError && rest?.value !== EMPTY_STRING;

  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }

    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);

    input.addEventListener('focus', handleFocus);
    input.addEventListener('blur', handleBlur);

    return () => {
      input.removeEventListener('focus', handleFocus);
      input.removeEventListener('blur', handleBlur);
    };
  }, []);

  return (
    <label
      htmlFor={inputId}
      className='w-full flex flex-col ~gap-1/1.5 relative'>
      {(isFocused || inputRef.current?.value) && (
        <p className='~text-[0.6rem]/[0.7rem] absolute -top-2 sm:-top-3 ~left-1/2 ~px-1.5/2 bg-gradient-to-b from-secondary-50 to-white dark:from-secondary-dark-200 via dark:to-transparent rounded-t-md animate-fadeIn tracking-wide text-gray-800 dark:text-secondary-dark-900'>
          {label || t('email')}
        </p>
      )}
      <input
        ref={inputRef}
        id={inputId}
        type={type}
        placeholder={isFocused ? EMPTY_STRING : (placeholder ?? t('email'))}
        className={`border rounded-lg ~px-2/4 py-2 outline-none ~text-xs/base responsive-shadow disabled:bg-gray-600 dark:disabled:bg-secondary-dark-400 dark:bg-secondary-dark-300 dark:border-secondary-dark-500 dark:text-secondary-dark-900 focus:dark:border-primary-dark-500 focus:dark:bg-secondary-dark-400 ${
          isInValidInput ? 'border-red-500 dark:border-red-400' : 'border-black'
        } ${additionalStyles}`}
        {...rest}
      />
      {hasError && error && (
        <small className='text-red-600 ~text-[0.65rem]/xs animate-fadeIn'>
          {error}
        </small>
      )}
    </label>
  );
};

export default EmailInput;
